import { getMonthName } from '@celerofinancas/core-ts';

import { InfoNotice } from '@shared/models/info-notice';

/**
 * Notices the dashboard's info card shows, card
 * [86e33z2ub](https://app.clickup.com/t/86e33z2ub).
 *
 * They live here, and not inside the component, because the card asks for content that is
 * "dynamic and parameterised" with a display condition per notice. This is the file to
 * touch to add, remove or reword one, and the component stays a way of presenting a list.
 *
 * ## The Open Finance call to action still has nowhere to go
 *
 * The second notice was held back at first for that reason, and it is on the screen now
 * because the carousel cannot be reviewed with a single notice — there are no dots and no
 * rotation to look at.
 *
 * The flow it should open does not exist in this project, and not by oversight: the
 * account header turns its edit icon off for the same reason (see `BankHeaderComponent`),
 * because the integration was not contracted for GetNet and the mock has no endpoint
 * behind it. The persona has no Open Finance connection either.
 *
 * **So the button does nothing, and that has to be decided before this reaches the
 * client.** Either it gets a destination or the notice loses its call to action. Asked on
 * the card; a `console` warning would be noise for whoever is reviewing the screen, and
 * silently swallowing the click is exactly what makes a dead end hard to spot, so the
 * action is left explicit and empty right here where it is read.
 *
 * @param today date the notices are built for, which decides which month the report is of
 * @returns the notices that apply
 */
export function buildInfoNotices(today: Date): InfoNotice[] {
  return [monthlyReportNotice(today), openFinanceNotice()];
}

/**
 * "Relatório DFM disponível", the first notice of the design.
 *
 * The month is the one that closed, which is why it is read from a date instead of being
 * written into the copy: in January it has to say December, and of the year before.
 *
 * @param today date the notice is built for
 * @returns the notice
 */
function monthlyReportNotice(today: Date): InfoNotice {
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const monthName = getMonthName(lastMonth.getMonth() + 1);

  return {
    id: 'monthly-report',
    title: 'Confira o desempenho mensal do seu negócio!',
    body: [
      {
        text: `Seu relatório DFM (Demonstrativo Financeiro Mensal) de ${monthName} já está disponível para consulta.`,
      },
    ],
  };
}

/**
 * "Conectar Open Finance", the second notice of the design.
 *
 * The body arrives in parts so "Open Finance" can be italic, as the design shows, without
 * the copy going through `innerHTML`.
 *
 * @returns the notice
 */
function openFinanceNotice(): InfoNotice {
  return {
    id: 'open-finance',
    title: 'Conecte todas as suas contas bancárias.',
    body: [
      { text: 'Traga suas contas de outros bancos pelo ' },
      { text: 'Open Finance', emphasis: true },
      { text: ' e acompanhe tudo em um lugar.' },
    ],
    cta: {
      label: 'Conectar Open Finance',
      // Sem destino: ver o bloco sobre o Open Finance no topo deste arquivo.
      action: () => undefined,
    },
  };
}
